/**
 * Compare fn arguments with previous one by applying `equalityCheck` to each arg
 * @param equalityCheck
 * @param prev
 * @param next
 * @returns {boolean}
 */
 export const areArgumentsEqual = (equalityCheck, prev, next) => {
    if (prev === null || next === null || prev.length !== next.length) {
        return false;
    }

    // Do this in a for loop (and not a `forEach` or an `every`) so we can determine equality as fast as possible.
    const { length } = prev;
    for (let i = 0; i < length; i++) {
        if (!equalityCheck(prev[i], next[i])) {
            return false;
        }
    }

    return true;
};

/**
 * Memoize one result with any number of arguments
 * Based on `defaultMemoize` from `reselect` lib
 * @param fn
 * @param areEqual
 * @returns {function(...[*]=)}
 */
export const memoizeOne = (fn, areEqual = Object.is) => {
    let lastArgs = null;
    let lastResult = null;

    // we reference arguments instead of spreading them for performance reasons
    return function() {
        /* eslint-disable prefer-rest-params, prefer-spread */
        // apply arguments instead of spreading for performance.
        if (!areArgumentsEqual(areEqual, lastArgs, arguments)) {
            lastResult = fn.apply(null, arguments);
        }

        lastArgs = arguments;

        return lastResult;
        /* eslint-enable prefer-rest-params, prefer-spread */
    };
};

/**
 * Memoize many results based on 1st argument. Equality check is reference equality
 * @param fn
 * @param cacheSize
 * @returns {function(...[*]=)}
 */
export const memoizeMany = (fn, cacheSize = 1000) => {
    const cache = new Map();

    return function(key) {
        if (cache.has(key)) {
            return cache.get(key);
        }

        // discard extra cache items
        if (cache.size === cacheSize) {
            cache.delete(cache.keys().next().value);
        }

        // eslint-disable-next-line prefer-spread, prefer-rest-params
        const result = fn.apply(null, arguments);
        cache.set(key, result);

        return result;
    };
};
